import { z } from 'zod';
import type { JSONObject, JSONValue } from '@ai-sdk/provider';
import type { UIDataTypes, UIMessage, UIMessagePart, UITools } from 'ai';
import { editorStateSchema } from './editor-state';

/**
 * Aligned with AI SDK wire validation in `ai/src/ui/validate-ui-messages.ts`, plus
 * Cotera-only parts (`editor-state`, `stored-system-message`) used on chat POST.
 * Update AI SDK branches when upgrading the `ai` package if part shapes change.
 */
const jsonValueSchema: z.ZodType<JSONValue> = z.lazy(() =>
  z.union([
    z.null(),
    z.string(),
    z.number(),
    z.boolean(),
    z.record(z.string(), jsonValueSchema.optional()),
    z.array(jsonValueSchema),
  ])
);

const providerMetadataSchema = z.record(
  z.string(),
  z.record(z.string(), jsonValueSchema.optional())
);

const toolMetadataSchema: z.ZodType<JSONObject> = z.record(
  z.string(),
  jsonValueSchema.optional()
);

export const uiMessagePartSchema: z.ZodType<
  UIMessagePart<UIDataTypes, UITools>
> = z.union([
  z.object({
    type: z.literal('text'),
    text: z.string(),
    state: z.enum(['streaming', 'done']).optional(),
    providerMetadata: providerMetadataSchema.optional(),
  }),
  z.object({
    type: z.literal('reasoning'),
    text: z.string(),
    state: z.enum(['streaming', 'done']).optional(),
    providerMetadata: providerMetadataSchema.optional(),
  }),
  z.object({
    type: z.literal('source-url'),
    sourceId: z.string(),
    url: z.string(),
    title: z.string().optional(),
    providerMetadata: providerMetadataSchema.optional(),
  }),
  z.object({
    type: z.literal('source-document'),
    sourceId: z.string(),
    mediaType: z.string(),
    title: z.string(),
    filename: z.string().optional(),
    providerMetadata: providerMetadataSchema.optional(),
  }),
  z.object({
    type: z.literal('file'),
    mediaType: z.string(),
    filename: z.string().optional(),
    url: z.string(),
    providerMetadata: providerMetadataSchema.optional(),
  }),
  z.object({
    type: z.literal('step-start'),
  }),
  z.object({
    type: z.literal('editor-state'),
    editorState: editorStateSchema,
  }),
  z.object({
    type: z.literal('stored-system-message'),
    payload: z.union([
      z.object({ t: z.literal('coco') }),
      z.object({ t: z.literal('ad-hoc'), message: z.string() }),
    ]),
  }),
  z.object({
    type: z.string().startsWith('data-'),
    id: z.string().optional(),
    data: z.unknown(),
  }),
  z.object({
    type: z.literal('dynamic-tool'),
    toolName: z.string(),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('input-streaming'),
    input: z.unknown().optional(),
    providerExecuted: z.boolean().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    approval: z.never().optional(),
  }),
  z.object({
    type: z.literal('dynamic-tool'),
    toolName: z.string(),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('input-available'),
    input: z.unknown(),
    providerExecuted: z.boolean().optional(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.never().optional(),
  }),
  z.object({
    type: z.literal('dynamic-tool'),
    toolName: z.string(),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('approval-requested'),
    input: z.unknown(),
    providerExecuted: z.boolean().optional(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.object({
      id: z.string(),
      approved: z.never().optional(),
      reason: z.never().optional(),
    }),
  }),
  z.object({
    type: z.literal('dynamic-tool'),
    toolName: z.string(),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('approval-responded'),
    input: z.unknown(),
    providerExecuted: z.boolean().optional(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.object({
      id: z.string(),
      approved: z.boolean(),
      reason: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('dynamic-tool'),
    toolName: z.string(),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('output-available'),
    input: z.unknown(),
    providerExecuted: z.boolean().optional(),
    output: z.unknown(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    resultProviderMetadata: providerMetadataSchema.optional(),
    preliminary: z.boolean().optional(),
    approval: z
      .object({
        id: z.string(),
        approved: z.literal(true),
        reason: z.string().optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal('dynamic-tool'),
    toolName: z.string(),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('output-error'),
    input: z.unknown().optional(),
    rawInput: z.unknown().optional(),
    providerExecuted: z.boolean().optional(),
    output: z.never().optional(),
    errorText: z.string(),
    callProviderMetadata: providerMetadataSchema.optional(),
    resultProviderMetadata: providerMetadataSchema.optional(),
    approval: z
      .object({
        id: z.string(),
        approved: z.literal(true),
        reason: z.string().optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.literal('dynamic-tool'),
    toolName: z.string(),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('output-denied'),
    input: z.unknown(),
    providerExecuted: z.boolean().optional(),
    // Cotera persists cancellation/denial results on output-denied parts (see completeToolCall).
    output: z.unknown().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.object({
      id: z.string(),
      approved: z.literal(false),
      reason: z.string().optional(),
    }),
  }),
  z.object({
    type: z.string().startsWith('tool-'),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('input-streaming'),
    providerExecuted: z.boolean().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    input: z.unknown().optional(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    approval: z.never().optional(),
  }),
  z.object({
    type: z.string().startsWith('tool-'),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('input-available'),
    providerExecuted: z.boolean().optional(),
    input: z.unknown(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.never().optional(),
  }),
  z.object({
    type: z.string().startsWith('tool-'),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('approval-requested'),
    input: z.unknown(),
    providerExecuted: z.boolean().optional(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.object({
      id: z.string(),
      approved: z.never().optional(),
      reason: z.never().optional(),
    }),
  }),
  z.object({
    type: z.string().startsWith('tool-'),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('approval-responded'),
    input: z.unknown(),
    providerExecuted: z.boolean().optional(),
    output: z.never().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.object({
      id: z.string(),
      approved: z.boolean(),
      reason: z.string().optional(),
    }),
  }),
  z.object({
    type: z.string().startsWith('tool-'),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('output-available'),
    providerExecuted: z.boolean().optional(),
    input: z.unknown(),
    output: z.unknown(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    resultProviderMetadata: providerMetadataSchema.optional(),
    preliminary: z.boolean().optional(),
    approval: z
      .object({
        id: z.string(),
        approved: z.literal(true),
        reason: z.string().optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.string().startsWith('tool-'),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('output-error'),
    providerExecuted: z.boolean().optional(),
    input: z.unknown().optional(),
    rawInput: z.unknown().optional(),
    output: z.never().optional(),
    errorText: z.string(),
    callProviderMetadata: providerMetadataSchema.optional(),
    resultProviderMetadata: providerMetadataSchema.optional(),
    approval: z
      .object({
        id: z.string(),
        approved: z.literal(true),
        reason: z.string().optional(),
      })
      .optional(),
  }),
  z.object({
    type: z.string().startsWith('tool-'),
    toolCallId: z.string(),
    toolMetadata: toolMetadataSchema.optional(),
    state: z.literal('output-denied'),
    providerExecuted: z.boolean().optional(),
    input: z.unknown(),
    // Cotera persists cancellation/denial results on output-denied parts (see completeToolCall).
    output: z.unknown().optional(),
    errorText: z.never().optional(),
    callProviderMetadata: providerMetadataSchema.optional(),
    approval: z.object({
      id: z.string(),
      approved: z.literal(false),
      reason: z.string().optional(),
    }),
  }),
]) as z.ZodType<UIMessagePart<UIDataTypes, UITools>>;

export const uiMessageFieldsSchema = z.object({
  id: z.string(),
  role: z.enum(['system', 'user', 'assistant']),
  // Streaming assistant rows are sometimes persisted before any part arrives.
  parts: z.array(uiMessagePartSchema),
  metadata: z.unknown().optional(),
});

/** Runtime validation for chat wire messages; typed as {@link UIMessage} at boundaries. */
export const uiMessageSchema: z.ZodType<UIMessage> =
  uiMessageFieldsSchema as z.ZodType<UIMessage>;

export const uiMessageArraySchema = z.array(uiMessageSchema);
